import Logger from './logger';
import ErrorTracker from './errorTracker';
import {
  FIREBASE_APP_CHECK_CSP_CONNECT_ORIGINS,
  FIREBASE_APP_CHECK_CSP_FRAME_ORIGINS,
  FIREBASE_APP_CHECK_CSP_SCRIPT_ORIGINS,
} from '../constants/appCheck';
import { LOCAL_DEV_CONNECT_SRC } from '../constants/localDevOrigins';

// Security header configuration
interface SecurityHeaderConfig {
  enableHSTS: boolean;
  enableXSSProtection: boolean;
  enableContentTypeOptions: boolean;
  enableFrameOptions: boolean;
  enableReferrerPolicy: boolean;
  enablePermissionsPolicy: boolean;
  enableCSP: boolean;
  hstsMaxAge: number;
  hstsIncludeSubDomains: boolean;
  hstsPreload: boolean;
  frameOptions: 'DENY' | 'SAMEORIGIN' | 'ALLOW-FROM';
  referrerPolicy: 'no-referrer' | 'no-referrer-when-downgrade' | 'origin' | 'origin-when-cross-origin' | 'same-origin' | 'strict-origin' | 'strict-origin-when-cross-origin' | 'unsafe-url';
  cspReportUri?: string;
  cspReportOnly: boolean;
}

// Security header result
interface SecurityHeaderResult {
  header: string;
  value: string;
  applied: boolean;
  error?: string;
}

// Security headers status
interface SecurityHeadersStatus {
  totalHeaders: number;
  appliedHeaders: number;
  failedHeaders: number;
  violations: number;
  lastUpdate: number;
  headers: SecurityHeaderResult[];
}

// CSP directive configuration
interface CSPDirective {
  directive: string;
  sources: string[];
  required: boolean;
}

class SecurityHeadersService {
  private static instance: SecurityHeadersService;
  private config: SecurityHeaderConfig;
  private status: SecurityHeadersStatus;
  private cspViolations: any[] = [];
  private isInitialized: boolean = false;

  // Comprehensive CSP directives
  private readonly cspDirectives: CSPDirective[] = [
    {
      directive: 'default-src',
      sources: ["'self'"],
      required: true
    },
    {
      directive: 'script-src',
      sources: [
        "'self'",
        "'unsafe-inline'", // Required for React
        "'unsafe-eval'", // Required for React development
        "https://*.tranzit.io",
        "https://*.firebase.com",
        "https://*.firebaseio.com",
        "https://www.googletagmanager.com",
        "https://cdn.jsdelivr.net",
        "https://js.stripe.com", // Required for Stripe.js
        "https://challenges.cloudflare.com", // B4 Turnstile registration widget
        ...FIREBASE_APP_CHECK_CSP_SCRIPT_ORIGINS, // A4 App Check reCAPTCHA v3
      ],
      required: true
    },
    {
      directive: 'style-src',
      sources: [
        "'self'",
        "'unsafe-inline'", // Required for CSS-in-JS
        "https://fonts.googleapis.com",
        "https://cdn.jsdelivr.net"
      ],
      required: true
    },
    {
      directive: 'font-src',
      sources: [
        "'self'",
        "https://fonts.gstatic.com",
        "https://cdn.jsdelivr.net"
      ],
      required: true
    },
    {
      directive: 'img-src',
      sources: [
        "'self'",
        "data:",
        "https:",
        "https://*.tranzit.io",
        "https://*.firebase.com",
        "https://*.mapbox.com"
      ],
      required: true
    },
    {
      directive: 'connect-src',
      sources: [
        "'self'",
        ...(process.env.NODE_ENV !== 'production' ? [...LOCAL_DEV_CONNECT_SRC] : []),
        "https://*.tranzit.io",
        "https://*.firebase.com",
        "https://*.firebaseio.com",
        "https://*.cloudfunctions.net", // PRODUCTION FIX: Allow Firebase Cloud Functions calls
        "https://*.run.app", // PRODUCTION FIX: Allow Cloud Run backend URLs
        ...(process.env.REACT_APP_BACKEND_URL ? [process.env.REACT_APP_BACKEND_URL] : []), // Required in production: set REACT_APP_BACKEND_URL
        "https://securetoken.googleapis.com",
        "https://identitytoolkit.googleapis.com",
        "https://*.googleapis.com",
        "https://www.google-analytics.com",
        "https://www.googletagmanager.com",
        "https://events.mapbox.com",
        "https://api.quickbooks.com",
        "https://www.routingnumbers.info",
        "https://api.stripe.com",
        "https://api.paypal.com",
        "https://api.squareup.com",
        "https://api.zelle.com",
        "wss://*.firebase.com",
        "wss://*.firebaseio.com",
        "https://api.mapbox.com",
        "https://mobile.fmcsa.dot.gov", // FMCSA API
        "https://safer.fmcsa.dot.gov", // SAFER website
        "https://challenges.cloudflare.com", // B4 Turnstile
        ...FIREBASE_APP_CHECK_CSP_CONNECT_ORIGINS, // A4 App Check
        // CORS proxy services removed from production - security risk
      ],
      required: true
    },
    {
      directive: 'frame-src',
      sources: [
        "'self'",
        "https://*.tranzit.io",
        "https://*.firebase.com",
        "https://*.firebaseio.com",
        "https://js.stripe.com",
        "https://www.paypal.com",
        "https://squareup.com",
        "https://challenges.cloudflare.com", // B4 Turnstile iframe
        ...FIREBASE_APP_CHECK_CSP_FRAME_ORIGINS, // A4 App Check reCAPTCHA
      ],
      required: true
    },
    {
      directive: 'worker-src',
      sources: [
        "'self'",
        "blob:",
        "https://*.firebase.com",
        "https://*.firebaseio.com"
      ],
      required: true
    },
    {
      directive: 'object-src',
      sources: ["'none'"],
      required: true
    },
    {
      directive: 'media-src',
      sources: ["'self'", "https:"],
      required: false
    },
    {
      directive: 'worker-src',
      sources: ["'self'", "blob:"],
      required: false
    },
    {
      directive: 'child-src',
      sources: ["'self'"],
      required: false
    },
    {
      directive: 'form-action',
      sources: ["'self'"],
      required: false
    },
    {
      directive: 'frame-ancestors',
      sources: ["'none'"],
      required: true
    },
    {
      directive: 'base-uri',
      sources: ["'self'"],
      required: true
    },
    {
      directive: 'upgrade-insecure-requests',
      sources: [],
      required: false
    }
  ];

  constructor() {
    this.config = {
      enableHSTS: true,
      enableXSSProtection: true,
      enableContentTypeOptions: true,
      enableFrameOptions: true,
      enableReferrerPolicy: true,
      enablePermissionsPolicy: true,
      enableCSP: true,
      hstsMaxAge: 31536000, // 1 year
      hstsIncludeSubDomains: true,
      hstsPreload: true,
      frameOptions: 'SAMEORIGIN',
      referrerPolicy: 'strict-origin-when-cross-origin',
      cspReportOnly: false
    };

    this.status = {
      totalHeaders: 0,
      appliedHeaders: 0,
      failedHeaders: 0,
      violations: 0,
      lastUpdate: 0,
      headers: []
    };
  }

  public static getInstance(): SecurityHeadersService {
    if (!SecurityHeadersService.instance) {
      SecurityHeadersService.instance = new SecurityHeadersService();
    }
    return SecurityHeadersService.instance;
  }

  /**
   * Initialize security headers service
   */
  public initialize(config: Partial<SecurityHeaderConfig> = {}): void {
    try {
      this.config = { ...this.config, ...config };
      this.applySecurityHeaders();
      this.setupCSPViolationReporting();
      this.isInitialized = true;

      Logger.info('Security Headers Service initialized', {
        config: this.config,
        appliedHeaders: this.status.appliedHeaders
      });

      ErrorTracker.trackDataProtectionEvent(
        'security_headers_initialized',
        'internal',
        undefined,
        { appliedHeaders: this.status.appliedHeaders }
      );

    } catch (error) {
      Logger.error('Failed to initialize Security Headers Service', error instanceof Error ? error : new Error('Unknown error'));
      ErrorTracker.trackError(
        error instanceof Error ? error : new Error('Security Headers Service initialization failed'),
        'SecurityHeadersService',
        { component: 'SecurityHeadersService', action: 'initialize' }
      );
    }
  }

  /**
   * Apply all security headers
   */
  private applySecurityHeaders(): void {
    const headers: SecurityHeaderResult[] = [];

    // Apply HSTS header
    if (this.config.enableHSTS) {
      headers.push(this.applyHSTSHeader());
    }

    // Apply XSS Protection header
    if (this.config.enableXSSProtection) {
      headers.push(this.applyXSSProtectionHeader());
    }

    // Apply Content Type Options header
    if (this.config.enableContentTypeOptions) {
      headers.push(this.applyContentTypeOptionsHeader());
    }

    // Apply Frame Options header
    if (this.config.enableFrameOptions) {
      headers.push(this.applyFrameOptionsHeader());
    }

    // Apply Referrer Policy header
    if (this.config.enableReferrerPolicy) {
      headers.push(this.applyReferrerPolicyHeader());
    }

    // Apply Permissions Policy header
    if (this.config.enablePermissionsPolicy) {
      headers.push(this.applyPermissionsPolicyHeader());
    }

    // Apply CSP header
    if (this.config.enableCSP) {
      headers.push(this.applyCSPHeader());
    }

    this.status.headers = headers;
    this.status.totalHeaders = headers.length;
    this.status.appliedHeaders = headers.filter(h => h.applied).length;
    this.status.failedHeaders = headers.filter(h => !h.applied).length;
    this.status.lastUpdate = Date.now();
  }

  /**
   * Apply HSTS header
   */
  private applyHSTSHeader(): SecurityHeaderResult {
    try {
      const hstsValue = `max-age=${this.config.hstsMaxAge}${this.config.hstsIncludeSubDomains ? '; includeSubDomains' : ''}${this.config.hstsPreload ? '; preload' : ''}`;
      
      // Note: HSTS can only be set by server, not client-side
      // This is for monitoring purposes
      return {
        header: 'Strict-Transport-Security',
        value: hstsValue,
        applied: false, // Cannot be applied client-side
        error: 'HSTS must be set by server'
      };
    } catch (error) {
      return {
        header: 'Strict-Transport-Security',
        value: '',
        applied: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Apply XSS Protection header
   */
  private applyXSSProtectionHeader(): SecurityHeaderResult {
    try {
      // Note: XSS Protection is deprecated but still useful for older browsers
      // Modern browsers use CSP instead
      return {
        header: 'X-XSS-Protection',
        value: '1; mode=block',
        applied: false, // Cannot be applied client-side
        error: 'XSS Protection must be set by server'
      };
    } catch (error) {
      return {
        header: 'X-XSS-Protection',
        value: '',
        applied: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Apply Content Type Options header
   */
  private applyContentTypeOptionsHeader(): SecurityHeaderResult {
    try {
      return {
        header: 'X-Content-Type-Options',
        value: 'nosniff',
        applied: false, // Cannot be applied client-side
        error: 'Content Type Options must be set by server'
      };
    } catch (error) {
      return {
        header: 'X-Content-Type-Options',
        value: '',
        applied: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Apply Frame Options header
   */
  private applyFrameOptionsHeader(): SecurityHeaderResult {
    try {
      return {
        header: 'X-Frame-Options',
        value: this.config.frameOptions,
        applied: false, // Cannot be applied client-side
        error: 'Frame Options must be set by server'
      };
    } catch (error) {
      return {
        header: 'X-Frame-Options',
        value: '',
        applied: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Apply Referrer Policy header
   */
  private applyReferrerPolicyHeader(): SecurityHeaderResult {
    try {
      return {
        header: 'Referrer-Policy',
        value: this.config.referrerPolicy,
        applied: false, // Cannot be applied client-side
        error: 'Referrer Policy must be set by server'
      };
    } catch (error) {
      return {
        header: 'Referrer-Policy',
        value: '',
        applied: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Apply Permissions Policy header
   */
  private applyPermissionsPolicyHeader(): SecurityHeaderResult {
    try {
      const permissionsPolicy = [
        'camera=()',
        'microphone=()',
        'geolocation=(self)',
        'payment=()',
        'usb=()',
        'magnetometer=()',
        'gyroscope=()',
        'accelerometer=()',
        'ambient-light-sensor=()',
        'autoplay=()',
        'battery=()',
        'display-capture=()',
        'fullscreen=(self)',
        'picture-in-picture=()',
        'publickey-credentials-get=()',
        'screen-wake-lock=()',
        'web-share=()',
        'xr-spatial-tracking=()'
      ].join(', ');

      return {
        header: 'Permissions-Policy',
        value: permissionsPolicy,
        applied: false, // Cannot be applied client-side
        error: 'Permissions Policy must be set by server'
      };
    } catch (error) {
      return {
        header: 'Permissions-Policy',
        value: '',
        applied: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Apply Content Security Policy header
   */
  private applyCSPHeader(): SecurityHeaderResult {
    try {
      const cspValue = this.generateCSPValue();
      
      // Apply CSP via meta tag (client-side)
      this.applyCSPMetaTag(cspValue);

      return {
        header: 'Content-Security-Policy',
        value: cspValue,
        applied: true,
      };
    } catch (error) {
      return {
        header: 'Content-Security-Policy',
        value: '',
        applied: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate CSP value from directives.
   * Skip upgrade-insecure-requests only when host is localhost or an IP so local dev can use http://localhost:3000
   * (no cert). Production (real domain) still gets the directive for SOC 2 / HTTPS enforcement.
   */
  private generateCSPValue(): string {
    const isDevOrLocalHost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(window.location.hostname));
    const directives = this.cspDirectives
      .filter(directive => directive.required || this.config.enableCSP)
      .filter(directive =>
        directive.directive !== 'upgrade-insecure-requests' || !isDevOrLocalHost
      )
      .map(directive => {
        if (directive.sources.length === 0) {
          return directive.directive;
        }
        return `${directive.directive} ${directive.sources.join(' ')}`;
      });

    let cspValue = directives.join('; ');

    // Add report-uri if configured
    if (this.config.cspReportUri) {
      cspValue += `; report-uri ${this.config.cspReportUri}`;
    }

    // Add report-only if configured
    if (this.config.cspReportOnly) {
      cspValue += '; report-only';
    }

    return cspValue;
  }

  /**
   * Apply CSP via meta tag
   */
  private applyCSPMetaTag(cspValue: string): void {
    try {
      // Remove existing CSP meta tag
      const existingMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
      if (existingMeta) {
        existingMeta.remove();
      }

      // Create new CSP meta tag
      const meta = document.createElement('meta');
      meta.setAttribute('http-equiv', 'Content-Security-Policy');
      meta.setAttribute('content', cspValue);
      
      // Add to document head
      document.head.appendChild(meta);

      Logger.info('CSP meta tag applied', { cspValue });
    } catch (error) {
      Logger.error('Failed to apply CSP meta tag', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Set up CSP violation reporting
   */
  private setupCSPViolationReporting(): void {
    try {
      document.addEventListener('securitypolicyviolation', (event) => {
        const violation = {
          blockedURI: event.blockedURI,
          violatedDirective: event.violatedDirective,
          effectiveDirective: event.effectiveDirective,
          originalPolicy: event.originalPolicy,
          referrer: event.referrer,
          statusCode: event.statusCode,
          timestamp: Date.now()
        };

        this.cspViolations.push(violation);
        this.status.violations++;

        Logger.warn('CSP violation detected', violation);

        ErrorTracker.trackDataProtectionEvent(
          'csp_violation',
          'high',
          undefined,
          {
            blockedURI: event.blockedURI,
            violatedDirective: event.violatedDirective,
            effectiveDirective: event.effectiveDirective
          }
        );
      });

      Logger.info('CSP violation reporting set up');
    } catch (error) {
      Logger.error('Failed to set up CSP violation reporting', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Get security headers status
   */
  public getStatus(): SecurityHeadersStatus {
    return { ...this.status };
  }

  /**
   * Get CSP violations
   */
  public getCSPViolations(): any[] {
    return [...this.cspViolations];
  }

  /**
   * Clear CSP violations
   */
  public clearCSPViolations(): void {
    this.cspViolations = [];
    this.status.violations = 0;
    Logger.info('CSP violations cleared');
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<SecurityHeaderConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.applySecurityHeaders();
    Logger.info('Security headers configuration updated');
  }

  /**
   * Get current configuration
   */
  public getConfig(): SecurityHeaderConfig {
    return { ...this.config };
  }

  /**
   * Check if service is initialized
   */
  public isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get CSP directives
   */
  public getCSPDirectives(): CSPDirective[] {
    return [...this.cspDirectives];
  }

  /**
   * Validate CSP directive
   */
  public validateCSPDirective(directive: string, sources: string[]): boolean {
    try {
      const cspDirective = this.cspDirectives.find(d => d.directive === directive);
      if (!cspDirective) {
        return false;
      }

      // Basic validation - check if sources are valid
      const validSources = ['self', 'unsafe-inline', 'unsafe-eval', 'none', 'data:', 'https:', 'http:'];
      const hasValidSources = sources.every(source => 
        validSources.includes(source) || 
        source.startsWith('https://') || 
        source.startsWith('http://') ||
        source.startsWith('blob:') ||
        source.startsWith('wss://') ||
        source.startsWith('ws://')
      );

      return hasValidSources;
    } catch (error) {
      Logger.error('Failed to validate CSP directive', error instanceof Error ? error : new Error('Unknown error'));
      return false;
    }
  }
}

// Export singleton instance
const securityHeadersService = new SecurityHeadersService();
export default securityHeadersService;
export { type SecurityHeaderConfig, type SecurityHeaderResult, type SecurityHeadersStatus, type CSPDirective };
